import mitt from 'mitt';

// Create a typed event bus for decoupled component communication
const eventBus = mitt();

// Event types for better debugging and IDE support
export const Events = {
  // Command bar events
  COMMAND_BAR_OPEN: 'commandBar:open',
  COMMAND_BAR_CLOSE: 'commandBar:close',
  COMMAND_BAR_SEARCH: 'commandBar:search',
  COMMAND_BAR_EXECUTE: 'commandBar:execute',

  // Search events
  GLOBAL_SEARCH: 'search:global',
  NOTES_SEARCH: 'search:notes',
  PROJECTS_SEARCH: 'search:projects',
  BOOKMARKS_SEARCH: 'search:bookmarks',

  // Notes events
  NOTE_CREATED: 'notes:created',
  NOTE_UPDATED: 'notes:updated',
  NOTE_DELETED: 'notes:deleted',
  NOTE_SELECTED: 'notes:selected',

  // Project events
  PROJECT_CREATED: 'projects:created',
  PROJECT_UPDATED: 'projects:updated',
  PROJECT_DELETED: 'projects:deleted',
  PROJECT_SELECTED: 'projects:selected',

  // Voice events
  VOICE_RECORDING_START: 'voice:recording_start',
  VOICE_RECORDING_STOP: 'voice:recording_stop',
  VOICE_PLAYBACK_START: 'voice:playback_start',
  VOICE_PLAYBACK_STOP: 'voice:playback_stop',

  // UI events
  SIDEBAR_TOGGLE: 'ui:sidebar_toggle',
  THEME_CHANGE: 'ui:theme_change',
  MODAL_OPEN: 'ui:modal_open',
  MODAL_CLOSE: 'ui:modal_close',
  PAGE_CHANGE: 'ui:page_change',

  // Data refresh events
  REFRESH_NOTES: 'data:refresh_notes',
  REFRESH_PROJECTS: 'data:refresh_projects',
  REFRESH_BOOKMARKS: 'data:refresh_bookmarks',

  // Auth events
  AUTH_LOGIN: 'auth:login',
  AUTH_LOGOUT: 'auth:logout',
  AUTH_TOKEN_REFRESH: 'auth:token_refresh'
};

// Wrapper with logging for development
class EventBus {
  constructor(emitter) {
    this.emitter = emitter;
    this.listeners = new Map(); // Track listeners for debugging
  }

  // Emit an event
  emit(type, data) {
    // Silent event emission in production
    this.emitter.emit(type, data);
  }

  // Listen to an event
  on(type, handler) {
    if (process.env.NODE_ENV === 'development') {
      if (!this.listeners.has(type)) {
        this.listeners.set(type, []);
      }
      this.listeners.get(type).push(handler);
      // Reduced logging for listeners
      // console.log(`[EventBus] Listener added for: ${type} (total: ${this.listeners.get(type).length})`);
    }
    
    this.emitter.on(type, handler);

    // Return unsubscribe function
    return () => {
      this.off(type, handler);
    };
  }

  // Remove listener
  off(type, handler) {
    if (process.env.NODE_ENV === 'development') {
      const typeListeners = this.listeners.get(type);
      if (typeListeners) {
        const index = typeListeners.indexOf(handler);
        if (index > -1) {
          typeListeners.splice(index, 1);
          // console.log(`[EventBus] Listener removed for: ${type} (remaining: ${typeListeners.length})`);
        }
      }
    }
    
    this.emitter.off(type, handler);
  }

  // Listen once
  once(type, handler) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[EventBus] One-time listener added for: ${type}`);
    }
    
    const wrappedHandler = (data) => {
      handler(data);
      if (process.env.NODE_ENV === 'development') {
        console.log(`[EventBus] One-time listener executed and removed: ${type}`);
      }
    };
    
    this.emitter.on(type, wrappedHandler);

    // Return unsubscribe function
    return () => {
      this.emitter.off(type, wrappedHandler);
    };
  }

  // Clear all listeners (useful for cleanup)
  clear() {
    if (process.env.NODE_ENV === 'development') {
      console.log('[EventBus] Clearing all listeners');
    }
    this.emitter.all.clear();
    this.listeners.clear();
  }

  // Get debug info
  getDebugInfo() {
    if (process.env.NODE_ENV === 'development') {
      const info = {};
      for (const [event, handlers] of this.listeners.entries()) {
        info[event] = handlers.length;
      }
      return info;
    }
    return {};
  }
}

// Export the wrapped event bus
const wrappedEventBus = new EventBus(eventBus);
export default wrappedEventBus;

// Export raw emitter for direct access if needed
export const rawEventBus = eventBus;